import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getBeneficiaries,
  getBeneficiary,
  createBeneficiary,
  updateBeneficiary,
  deleteBeneficiary,
  getSponsorships,
  getSponsorship,
  createSponsorship,
  updateSponsorship,
  deleteSponsorship,
  createGuardian,
  getSchools,
  getSchool,
  createSchool,
  updateSchool,
  deleteSchool,
  getHospitals,
  getHospital,
  createHospital,
  updateHospital,
  deleteHospital,
  getMosques,
  getMosque,
  createMosque,
  updateMosque,
  deleteMosque,
  getOrphanages,
  getOrphanage,
  createOrphanage,
  updateOrphanage,
  deleteOrphanage,
  getHospitalOrphans,
  createHospitalOrphan,
  deleteHospitalOrphan,
  closeFacility,
  reopenFacility,
  getMosqueOrphans,
  createMosqueOrphan,
  deleteMosqueOrphan,
  getOrphanageOrphans,
  createOrphanageOrphan,
  deleteOrphanageOrphan,
  getBeneficiaryHospitals,
  getBeneficiaryMosques,
  getBeneficiaryOrphanages,
  getMedicalReports,
  createMedicalReport,
  getMosqueReports,
  createMosqueReport,
  updateMosqueReport,
  deleteMosqueReport,
  getOrphanageReports,
  createOrphanageReport,
  updateOrphanageReport,
  deleteOrphanageReport,
  getGalleryItems,
  createGalleryItem,
  getSponsorshipTypes,
  getSponsorshipConditions,
  getSponsorshipCosts,
  getSponsorshipPriorities,
  getSponsorshipDurations,
  getSponsorshipStatuses,
  getContacts,
  getSchoolStudent,
  getSchoolStudents,
  getSchoolOrphans,
  createSchoolStudent,
  updateSchoolStudent,
  deleteSchoolStudent,
  getSchoolReports,
  createSchoolReport,
  updateSchoolReport,
  deleteSchoolReport,
  getGuardianFatherStatus,
  getGuardianMotherStatus,
  getGuardianRelationship,
  getLocationConditions,
  getLocations,
  createSponsorshipPriority,
  updateSponsorshipPriority,
  deleteSponsorshipPriority,
  createSponsorshipStatus,
  updateSponsorshipStatus,
  deleteSponsorshipStatus,
  createSponsorshipType,
  updateSponsorshipType,
  deleteSponsorshipType,
  createSponsorshipCondition,
  updateSponsorshipCondition,
  deleteSponsorshipCondition,
  createSponsorshipCost,
  updateSponsorshipCost,
  deleteSponsorshipCost,
  createSponsorshipDuration,
  updateSponsorshipDuration,
  deleteSponsorshipDuration,
  // Sponsorship Management (Shared Sponsorships)
  getSponsorshipManagementStats,
  getSponsorshipsForManagement,
  getSponsorshipDetail,
  createSharedSponsorship,
  createSponsorshipsBatch,
  validateSponsorshipItems,
  endSponsorship,
  changeSponsorshipPayment,
  getDonorsWithRemainingDonations,
  getBeneficiaryStatusLogs,
  getBeneficiaryFeedback,
  createBeneficiaryFeedback,
  updateBeneficiaryFeedback,
  deleteBeneficiaryFeedback,
  getBeneficiarySupportingDocs,
  createBeneficiarySupportingDoc,
  approveBeneficiarySupportingDoc,
  deleteBeneficiarySupportingDoc
} from 'lib/supabaseQueries';
import { useAuthContext } from 'context/authContext';
import useBranchPermission from './useBranchPermission';
import { toast } from 'react-toastify';

// Hook to fetch all beneficiaries/orphans
// Stable empty-array reference. Returning a fresh `[]` on every render while
// the query loads makes any react-table consumer (e.g. SelectBeneficiaryModal,
// the sponsorship-donation table) see a new `data` reference each render, which
// triggers react-table's autoReset* effects to dispatch on every render ->
// "Maximum update depth exceeded". A shared constant keeps the reference stable.
const EMPTY_ARRAY = [];

export function useBeneficiaries(filters = {}) {
  const { organization, isInitialized } = useAuthContext();
  const { branchId, isMainBranch } = useBranchPermission();
  // Main branch (HQ) reads beneficiaries across all branches
  const effectiveBranchId = isMainBranch ? null : branchId;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['beneficiaries', organization?.id, effectiveBranchId, filters],
    queryFn: () =>
      getBeneficiaries(organization.id, effectiveBranchId, filters),
    enabled: isInitialized && !!organization?.id
  });

  return {
    beneficiaries: data?.data || EMPTY_ARRAY,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch single beneficiary/orphan
export function useBeneficiary(id) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['beneficiary', id],
    queryFn: () => getBeneficiary(id),
    enabled: !!id
  });

  return {
    beneficiary: data?.data || null,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create beneficiary
export function useCreateBeneficiary() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async beneficiaryData => {
      const result = await createBeneficiary({
        ...beneficiaryData,
        branch_id: branchId
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['beneficiaries'] });
      toast.success('Beneficiary created successfully');
    },
    onError: error => {
      console.error('Create beneficiary error:', error);
      toast.error('Failed to create beneficiary');
    }
  });

  return {
    createBeneficiary: mutation.mutate,
    createBeneficiaryAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update beneficiary
export function useUpdateBeneficiary() {
  const queryClient = useQueryClient();
  const { user } = useAuthContext();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      // Pass user info for audit logging
      const userInfo = user
        ? {
            id: user.id,
            first_name: user.first_name,
            last_name: user.last_name
          }
        : null;
      const result = await updateBeneficiary(id, data, userInfo);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['beneficiaries'] });
      await queryClient.invalidateQueries({
        queryKey: ['beneficiary', variables.id]
      });
      await queryClient.invalidateQueries({
        queryKey: ['beneficiaryStatusLogs', variables.id]
      });
      toast.success('Beneficiary updated successfully');
    },
    onError: error => {
      console.error('Update beneficiary error:', error);
      toast.error('Failed to update beneficiary');
    }
  });

  return {
    updateBeneficiary: mutation.mutate,
    updateBeneficiaryAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete beneficiary
export function useDeleteBeneficiary() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteBeneficiary(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['beneficiaries'] });
      toast.success('Beneficiary deleted successfully');
    },
    onError: error => {
      console.error('Delete beneficiary error:', error);
      toast.error('Failed to delete beneficiary');
    }
  });

  return {
    deleteBeneficiary: mutation.mutate,
    deleteBeneficiaryAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

// Hook to get beneficiary status change logs
export function useBeneficiaryStatusLogs(beneficiaryId) {
  return useQuery({
    queryKey: ['beneficiaryStatusLogs', beneficiaryId],
    queryFn: async () => {
      if (!beneficiaryId) return [];
      const result = await getBeneficiaryStatusLogs(beneficiaryId);
      if (result.error) {
        throw new Error(result.error);
      }
      return result.data || [];
    },
    enabled: !!beneficiaryId
  });
}

/* -------------------------------------------------------------------------- */
/*                       BENEFICIARY FEEDBACK / NOTES                         */
/* -------------------------------------------------------------------------- */

const EMPTY_FEEDBACK = [];

export function useBeneficiaryFeedback(beneficiaryId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['beneficiaryFeedback', beneficiaryId],
    queryFn: () => getBeneficiaryFeedback(beneficiaryId),
    enabled: !!beneficiaryId
  });

  return {
    feedback: data?.data || EMPTY_FEEDBACK,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

export function useCreateBeneficiaryFeedback() {
  const queryClient = useQueryClient();
  const { organization } = useAuthContext();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async feedbackData => {
      const result = await createBeneficiaryFeedback({
        ...feedbackData,
        branch_id: branchId,
        organisation_id: organization?.id
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['beneficiaryFeedback']
      });
      toast.success('Note created successfully');
    },
    onError: error => {
      console.error('Create beneficiary feedback error:', error);
      toast.error('Failed to create note');
    }
  });

  return {
    createFeedback: mutation.mutate,
    createFeedbackAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

export function useUpdateBeneficiaryFeedback() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateBeneficiaryFeedback(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['beneficiaryFeedback']
      });
      toast.success('Note updated successfully');
    },
    onError: error => {
      console.error('Update beneficiary feedback error:', error);
      toast.error('Failed to update note');
    }
  });

  return {
    updateFeedback: mutation.mutate,
    updateFeedbackAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

export function useDeleteBeneficiaryFeedback() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteBeneficiaryFeedback(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['beneficiaryFeedback']
      });
      toast.success('Note deleted successfully');
    },
    onError: error => {
      console.error('Delete beneficiary feedback error:', error);
      toast.error('Failed to delete note');
    }
  });

  return {
    deleteFeedback: mutation.mutate,
    deleteFeedbackAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                  BENEFICIARY SUPPORTING DOCUMENTS                         */
/* -------------------------------------------------------------------------- */

export function useBeneficiarySupportingDocs(beneficiaryId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['beneficiarySupportingDocs', beneficiaryId],
    queryFn: () => getBeneficiarySupportingDocs(beneficiaryId),
    enabled: !!beneficiaryId
  });

  return {
    documents: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

export function useCreateBeneficiarySupportingDoc() {
  const queryClient = useQueryClient();
  const { organization } = useAuthContext();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async docData => {
      const result = await createBeneficiarySupportingDoc({
        ...docData,
        branch_id: branchId,
        organisation_id: organization?.id
      });
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['beneficiarySupportingDocs']
      });
      toast.success('Document uploaded successfully');
    },
    onError: error => {
      console.error('Create supporting document error:', error);
      toast.error('Failed to upload document');
    }
  });

  return {
    createDocument: mutation.mutate,
    createDocumentAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

export function useApproveBeneficiarySupportingDoc() {
  const queryClient = useQueryClient();
  const { user } = useAuthContext();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await approveBeneficiarySupportingDoc(id, user?.id);
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['beneficiarySupportingDocs']
      });
      toast.success('Document approved');
    },
    onError: error => {
      console.error('Approve supporting document error:', error);
      toast.error('Failed to approve document');
    }
  });

  return {
    approveDocument: mutation.mutate,
    approveDocumentAsync: mutation.mutateAsync,
    isApproving: mutation.isPending,
    error: mutation.error
  };
}

export function useDeleteBeneficiarySupportingDoc() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteBeneficiarySupportingDoc(id);
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['beneficiarySupportingDocs']
      });
      toast.success('Document deleted');
    },
    onError: error => {
      console.error('Delete supporting document error:', error);
      toast.error('Failed to delete document');
    }
  });

  return {
    deleteDocument: mutation.mutate,
    deleteDocumentAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                           SPONSORSHIPS                                     */
/* -------------------------------------------------------------------------- */

// Hook to fetch all sponsorships
export function useSponsorships() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId, isMainBranch } = useBranchPermission();
  // Main branch (HQ) reads sponsorships across all branches
  const effectiveBranchId = isMainBranch ? null : branchId;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['sponsorships', organization?.id, effectiveBranchId],
    queryFn: () => getSponsorships(organization.id, effectiveBranchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    sponsorships: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch single sponsorship
export function useSponsorship(id) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['sponsorship', id],
    queryFn: () => getSponsorship(id),
    enabled: !!id
  });

  return {
    sponsorship: data?.data || null,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create sponsorship
export function useCreateSponsorship() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async sponsorshipData => {
      const result = await createSponsorship({
        ...sponsorshipData,
        branch_id: branchId
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sponsorships'] });
      await queryClient.invalidateQueries({ queryKey: ['donorSponsorships'] });
      await queryClient.invalidateQueries({ queryKey: ['beneficiaries'] });
      toast.success('Sponsorship created successfully');
    },
    onError: error => {
      console.error('Create sponsorship error:', error);
      toast.error('Failed to create sponsorship');
    }
  });

  return {
    createSponsorship: mutation.mutate,
    createSponsorshipAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update sponsorship
export function useUpdateSponsorship() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateSponsorship(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['sponsorships'] });
      await queryClient.invalidateQueries({
        queryKey: ['sponsorship', variables.id]
      });
      await queryClient.invalidateQueries({ queryKey: ['donorSponsorships'] });
      toast.success('Sponsorship updated successfully');
    },
    onError: error => {
      console.error('Update sponsorship error:', error);
      toast.error('Failed to update sponsorship');
    }
  });

  return {
    updateSponsorship: mutation.mutate,
    updateSponsorshipAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete sponsorship
export function useDeleteSponsorship() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteSponsorship(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sponsorships'] });
      await queryClient.invalidateQueries({ queryKey: ['donorSponsorships'] });
      toast.success('Sponsorship deleted successfully');
    },
    onError: error => {
      console.error('Delete sponsorship error:', error);
      toast.error('Failed to delete sponsorship');
    }
  });

  return {
    deleteSponsorship: mutation.mutate,
    deleteSponsorshipAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

// Hook to create guardian
export function useCreateGuardian() {
  const mutation = useMutation({
    mutationFn: async guardianData => {
      const result = await createGuardian(guardianData);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      toast.success('Guardian created successfully');
    },
    onError: error => {
      console.error('Create guardian error:', error);
      toast.error('Failed to create guardian');
    }
  });

  return {
    createGuardian: mutation.mutate,
    createGuardianAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                           SCHOOLS                                          */
/* -------------------------------------------------------------------------- */

// Hook to fetch all schools
export function useSchools() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId, isMainBranch } = useBranchPermission();
  // Main branch (HQ) sees facilities across all branches
  const effectiveBranchId = isMainBranch ? null : branchId;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['schools', organization?.id, effectiveBranchId],
    queryFn: () => getSchools(organization.id, effectiveBranchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    schools: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch single school
export function useSchool(id) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['school', id],
    queryFn: () => getSchool(id),
    enabled: !!id
  });

  return {
    school: data?.data || null,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create school
export function useCreateSchool() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();
  const { user } = useAuthContext();

  const mutation = useMutation({
    mutationFn: async schoolData => {
      // Keep an explicitly chosen branch; fall back to the viewed branch, then
      // the creator's own branch ("All Branches" view has no branch to use)
      const result = await createSchool({
        ...schoolData,
        branch_id: schoolData.branch_id || branchId || user?.branch_id
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['schools'] });
      toast.success('School created successfully');
    },
    onError: error => {
      console.error('Create school error:', error);
      toast.error('Failed to create school');
    }
  });

  return {
    createSchool: mutation.mutate,
    createSchoolAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update school
export function useUpdateSchool() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateSchool(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['schools'] });
      await queryClient.invalidateQueries({
        queryKey: ['school', variables.id]
      });
      toast.success('School updated successfully');
    },
    onError: error => {
      console.error('Update school error:', error);
      toast.error('Failed to update school');
    }
  });

  return {
    updateSchool: mutation.mutate,
    updateSchoolAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete school
export function useDeleteSchool() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteSchool(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['schools'] });
      toast.success('School deleted successfully');
    },
    onError: error => {
      console.error('Delete school error:', error);
      toast.error('Failed to delete school');
    }
  });

  return {
    deleteSchool: mutation.mutate,
    deleteSchoolAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                           HOSPITALS                                        */
/* -------------------------------------------------------------------------- */

// Hook to fetch all hospitals
export function useHospitals() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId, isMainBranch } = useBranchPermission();
  // Main branch (HQ) sees facilities across all branches
  const effectiveBranchId = isMainBranch ? null : branchId;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['hospitals', organization?.id, effectiveBranchId],
    queryFn: () => getHospitals(organization.id, effectiveBranchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    hospitals: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch single hospital
export function useHospital(id) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['hospital', id],
    queryFn: () => getHospital(id),
    enabled: !!id
  });

  return {
    hospital: data?.data || null,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create hospital
export function useCreateHospital() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();
  const { user } = useAuthContext();

  const mutation = useMutation({
    mutationFn: async hospitalData => {
      const result = await createHospital({
        ...hospitalData,
        branch_id: hospitalData.branch_id || branchId || user?.branch_id
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hospitals'] });
      toast.success('Hospital created successfully');
    },
    onError: error => {
      console.error('Create hospital error:', error);
      toast.error('Failed to create hospital');
    }
  });

  return {
    createHospital: mutation.mutate,
    createHospitalAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update hospital
export function useUpdateHospital() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateHospital(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['hospitals'] });
      await queryClient.invalidateQueries({
        queryKey: ['hospital', variables.id]
      });
      toast.success('Hospital updated successfully');
    },
    onError: error => {
      console.error('Update hospital error:', error);
      toast.error('Failed to update hospital');
    }
  });

  return {
    updateHospital: mutation.mutate,
    updateHospitalAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete hospital
export function useDeleteHospital() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteHospital(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hospitals'] });
      toast.success('Hospital deleted successfully');
    },
    onError: error => {
      console.error('Delete hospital error:', error);
      toast.error('Failed to delete hospital');
    }
  });

  return {
    deleteHospital: mutation.mutate,
    deleteHospitalAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                           MOSQUES                                          */
/* -------------------------------------------------------------------------- */

// Hook to fetch all mosques
export function useMosques() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId, isMainBranch } = useBranchPermission();
  // Main branch (HQ) sees facilities across all branches
  const effectiveBranchId = isMainBranch ? null : branchId;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['mosques', organization?.id, effectiveBranchId],
    queryFn: () => getMosques(organization.id, effectiveBranchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    mosques: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch single mosque
export function useMosque(id) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['mosque', id],
    queryFn: () => getMosque(id),
    enabled: !!id
  });

  return {
    mosque: data?.data || null,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create mosque
export function useCreateMosque() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();
  const { user } = useAuthContext();

  const mutation = useMutation({
    mutationFn: async mosqueData => {
      const result = await createMosque({
        ...mosqueData,
        branch_id: mosqueData.branch_id || branchId || user?.branch_id
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['mosques'] });
      toast.success('Madrasah created successfully');
    },
    onError: error => {
      console.error('Create mosque error:', error);
      toast.error('Failed to create mosque');
    }
  });

  return {
    createMosque: mutation.mutate,
    createMosqueAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update mosque
export function useUpdateMosque() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateMosque(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['mosques'] });
      await queryClient.invalidateQueries({
        queryKey: ['mosque', variables.id]
      });
      toast.success('Madrasah updated successfully');
    },
    onError: error => {
      console.error('Update mosque error:', error);
      toast.error('Failed to update mosque');
    }
  });

  return {
    updateMosque: mutation.mutate,
    updateMosqueAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete mosque
export function useDeleteMosque() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteMosque(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['mosques'] });
      toast.success('Madrasah deleted successfully');
    },
    onError: error => {
      console.error('Delete mosque error:', error);
      toast.error('Failed to delete mosque');
    }
  });

  return {
    deleteMosque: mutation.mutate,
    deleteMosqueAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                           ORPHANAGES                                       */
/* -------------------------------------------------------------------------- */

// Hook to fetch all orphanages
export function useOrphanages() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId, isMainBranch } = useBranchPermission();
  // Main branch (HQ) sees facilities across all branches
  const effectiveBranchId = isMainBranch ? null : branchId;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['orphanages', organization?.id, effectiveBranchId],
    queryFn: () => getOrphanages(organization.id, effectiveBranchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    orphanages: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch single orphanage
export function useOrphanage(id) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['orphanage', id],
    queryFn: () => getOrphanage(id),
    enabled: !!id
  });

  return {
    orphanage: data?.data || null,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create orphanage
export function useCreateOrphanage() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();
  const { user } = useAuthContext();

  const mutation = useMutation({
    mutationFn: async orphanageData => {
      const result = await createOrphanage({
        ...orphanageData,
        branch_id: orphanageData.branch_id || branchId || user?.branch_id
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['orphanages'] });
      toast.success('Orphanage created successfully');
    },
    onError: error => {
      console.error('Create orphanage error:', error);
      toast.error('Failed to create orphanage');
    }
  });

  return {
    createOrphanage: mutation.mutate,
    createOrphanageAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update orphanage
export function useUpdateOrphanage() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateOrphanage(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['orphanages'] });
      await queryClient.invalidateQueries({
        queryKey: ['orphanage', variables.id]
      });
      toast.success('Orphanage updated successfully');
    },
    onError: error => {
      console.error('Update orphanage error:', error);
      toast.error('Failed to update orphanage');
    }
  });

  return {
    updateOrphanage: mutation.mutate,
    updateOrphanageAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete orphanage
export function useDeleteOrphanage() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteOrphanage(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['orphanages'] });
      toast.success('Orphanage deleted successfully');
    },
    onError: error => {
      console.error('Delete orphanage error:', error);
      toast.error('Failed to delete orphanage');
    }
  });

  return {
    deleteOrphanage: mutation.mutate,
    deleteOrphanageAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                    INSTITUTION-BENEFICIARY LINKS                           */
/* -------------------------------------------------------------------------- */

// Hook to fetch hospital orphans
export function useHospitalOrphans(hospitalId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['hospitalOrphans', hospitalId],
    queryFn: () => getHospitalOrphans(hospitalId),
    enabled: !!hospitalId
  });

  return {
    hospitalOrphans: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create hospital orphan link
export function useCreateHospitalOrphan() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async linkData => {
      const result = await createHospitalOrphan(linkData);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hospitalOrphans'] });
      await queryClient.invalidateQueries({
        queryKey: ['beneficiaryHospitals']
      });
      toast.success('Hospital added successfully');
    },
    onError: error => {
      console.error('Create hospital orphan link error:', error);
      toast.error('Failed to add hospital');
    }
  });

  return {
    createHospitalOrphan: mutation.mutate,
    createHospitalOrphanAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete hospital orphan link
export function useDeleteHospitalOrphan() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteHospitalOrphan(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hospitalOrphans'] });
      await queryClient.invalidateQueries({
        queryKey: ['beneficiaryHospitals']
      });
      toast.success('Hospital removed successfully');
    },
    onError: error => {
      console.error('Delete hospital orphan link error:', error);
      toast.error('Failed to remove hospital');
    }
  });

  return {
    deleteHospitalOrphan: mutation.mutate,
    deleteHospitalOrphanAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

// Hook to fetch hospitals for a beneficiary
export function useBeneficiaryHospitals(beneficiaryId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['beneficiaryHospitals', beneficiaryId],
    queryFn: () => getBeneficiaryHospitals(beneficiaryId),
    enabled: !!beneficiaryId
  });

  return {
    beneficiaryHospitals: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch mosques for a beneficiary
export function useBeneficiaryMosques(beneficiaryId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['beneficiaryMosques', beneficiaryId],
    queryFn: () => getBeneficiaryMosques(beneficiaryId),
    enabled: !!beneficiaryId
  });

  return {
    beneficiaryMosques: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch orphanages for a beneficiary
export function useBeneficiaryOrphanages(beneficiaryId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['beneficiaryOrphanages', beneficiaryId],
    queryFn: () => getBeneficiaryOrphanages(beneficiaryId),
    enabled: !!beneficiaryId
  });

  return {
    beneficiaryOrphanages: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch mosque orphans
export function useMosqueOrphans(mosqueId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['mosqueOrphans', mosqueId],
    queryFn: () => getMosqueOrphans(mosqueId),
    enabled: !!mosqueId
  });

  return {
    mosqueOrphans: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create mosque orphan link
export function useCreateMosqueOrphan() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async linkData => {
      const result = await createMosqueOrphan(linkData);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['mosqueOrphans'] });
      await queryClient.invalidateQueries({ queryKey: ['beneficiaryMosques'] });
      toast.success('Madrasah added successfully');
    },
    onError: error => {
      console.error('Create mosque orphan link error:', error);
      toast.error('Failed to add mosque');
    }
  });

  return {
    createMosqueOrphan: mutation.mutate,
    createMosqueOrphanAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete mosque orphan link
export function useDeleteMosqueOrphan() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteMosqueOrphan(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['mosqueOrphans'] });
      await queryClient.invalidateQueries({ queryKey: ['beneficiaryMosques'] });
      toast.success('Madrasah removed successfully');
    },
    onError: error => {
      console.error('Delete mosque orphan link error:', error);
      toast.error('Failed to remove mosque');
    }
  });

  return {
    deleteMosqueOrphan: mutation.mutate,
    deleteMosqueOrphanAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

// Hook to fetch orphanage orphans
export function useOrphanageOrphans(orphanageId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['orphanageOrphans', orphanageId],
    queryFn: () => getOrphanageOrphans(orphanageId),
    enabled: !!orphanageId
  });

  return {
    orphanageOrphans: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create orphanage orphan link
export function useCreateOrphanageOrphan() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async linkData => {
      const result = await createOrphanageOrphan(linkData);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['orphanageOrphans'] });
      await queryClient.invalidateQueries({
        queryKey: ['beneficiaryOrphanages']
      });
      toast.success('Orphanage added successfully');
    },
    onError: error => {
      console.error('Create orphanage orphan link error:', error);
      toast.error('Failed to add orphanage');
    }
  });

  return {
    createOrphanageOrphan: mutation.mutate,
    createOrphanageOrphanAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete orphanage orphan link
export function useDeleteOrphanageOrphan() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteOrphanageOrphan(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['orphanageOrphans'] });
      await queryClient.invalidateQueries({
        queryKey: ['beneficiaryOrphanages']
      });
      toast.success('Orphanage removed successfully');
    },
    onError: error => {
      console.error('Delete orphanage orphan link error:', error);
      toast.error('Failed to remove orphanage');
    }
  });

  return {
    deleteOrphanageOrphan: mutation.mutate,
    deleteOrphanageOrphanAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                    SCHOOL STUDENTS & REPORTS                               */
/* -------------------------------------------------------------------------- */

// Hook to fetch school students
// Hook to fetch single school student by ID
export function useSchoolStudent(id) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['schoolStudent', id],
    queryFn: () => getSchoolStudent(id),
    enabled: !!id
  });

  return {
    schoolStudent: data?.data || null,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

export function useSchoolStudents(beneficiaryId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['schoolStudents', beneficiaryId],
    queryFn: () => getSchoolStudents(beneficiaryId),
    enabled: !!beneficiaryId
  });

  return {
    schoolStudents: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch orphans in a school (by school_id)
export function useSchoolOrphans(schoolId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['schoolOrphans', schoolId],
    queryFn: () => getSchoolOrphans(schoolId),
    enabled: !!schoolId
  });

  return {
    schoolOrphans: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create school student
export function useCreateSchoolStudent() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async studentData => {
      const result = await createSchoolStudent(studentData);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['schoolStudents'] });
      await queryClient.invalidateQueries({ queryKey: ['schoolOrphans'] });
      toast.success('Student added successfully');
    },
    onError: error => {
      console.error('Create school student error:', error);
      toast.error('Failed to add student');
    }
  });

  return {
    createSchoolStudent: mutation.mutate,
    createSchoolStudentAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update school student
export function useUpdateSchoolStudent() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateSchoolStudent(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['schoolStudents'] });
      toast.success('School student updated successfully');
    },
    onError: error => {
      console.error('Update school student error:', error);
      toast.error('Failed to update school student');
    }
  });

  return {
    updateSchoolStudent: mutation.mutate,
    updateSchoolStudentAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete school student
export function useDeleteSchoolStudent() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteSchoolStudent(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['schoolStudents'] });
      await queryClient.invalidateQueries({ queryKey: ['schoolOrphans'] });
      toast.success('Student removed successfully');
    },
    onError: error => {
      console.error('Delete school student error:', error);
      toast.error('Failed to remove student');
    }
  });

  return {
    deleteSchoolStudent: mutation.mutate,
    deleteSchoolStudentAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

// Hook to fetch school reports
export function useSchoolReports(beneficiaryId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['schoolReports', beneficiaryId],
    queryFn: () => getSchoolReports(beneficiaryId),
    enabled: !!beneficiaryId
  });

  return {
    schoolReports: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create school report
export function useCreateSchoolReport() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async reportData => {
      const result = await createSchoolReport({
        ...reportData,
        branch_id: branchId
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['schoolReports'] });
      toast.success('School report added successfully');
    },
    onError: error => {
      console.error('Create school report error:', error);
      toast.error('Failed to add school report');
    }
  });

  return {
    createSchoolReport: mutation.mutate,
    createSchoolReportAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update school report
export function useUpdateSchoolReport() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateSchoolReport(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['schoolReports'] });
      toast.success('School report updated successfully');
    },
    onError: error => {
      console.error('Update school report error:', error);
      toast.error('Failed to update school report');
    }
  });

  return {
    updateSchoolReport: mutation.mutate,
    updateSchoolReportAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete school report
export function useDeleteSchoolReport() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteSchoolReport(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['schoolReports'] });
      toast.success('School report deleted successfully');
    },
    onError: error => {
      console.error('Delete school report error:', error);
      toast.error('Failed to delete school report');
    }
  });

  return {
    deleteSchoolReport: mutation.mutate,
    deleteSchoolReportAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                    MEDICAL REPORTS & GALLERY                               */
/* -------------------------------------------------------------------------- */

// Hook to fetch medical reports
export function useMedicalReports(beneficiaryId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['medicalReports', beneficiaryId],
    queryFn: () => getMedicalReports(beneficiaryId),
    enabled: !!beneficiaryId
  });

  return {
    medicalReports: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create medical report
export function useCreateMedicalReport() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async reportData => {
      const result = await createMedicalReport({
        ...reportData,
        branch_id: branchId
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['medicalReports'] });
      toast.success('Medical report added successfully');
    },
    onError: error => {
      console.error('Create medical report error:', error);
      toast.error('Failed to add medical report');
    }
  });

  return {
    createMedicalReport: mutation.mutate,
    createMedicalReportAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                    MOSQUE REPORTS                                          */
/* -------------------------------------------------------------------------- */

// Hook to fetch mosque reports
export function useMosqueReports(beneficiaryId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['mosqueReports', beneficiaryId],
    queryFn: () => getMosqueReports(beneficiaryId),
    enabled: !!beneficiaryId
  });

  return {
    mosqueReports: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create mosque report
export function useCreateMosqueReport() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async reportData => {
      const result = await createMosqueReport({
        ...reportData,
        branch_id: branchId
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['mosqueReports'] });
      toast.success('Madrasah report added successfully');
    },
    onError: error => {
      console.error('Create mosque report error:', error);
      toast.error('Failed to add mosque report');
    }
  });

  return {
    createMosqueReport: mutation.mutate,
    createMosqueReportAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update mosque report
export function useUpdateMosqueReport() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateMosqueReport(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['mosqueReports'] });
      toast.success('Madrasah report updated successfully');
    },
    onError: error => {
      console.error('Update mosque report error:', error);
      toast.error('Failed to update mosque report');
    }
  });

  return {
    updateMosqueReport: mutation.mutate,
    updateMosqueReportAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete mosque report
export function useDeleteMosqueReport() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteMosqueReport(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['mosqueReports'] });
      toast.success('Madrasah report deleted successfully');
    },
    onError: error => {
      console.error('Delete mosque report error:', error);
      toast.error('Failed to delete mosque report');
    }
  });

  return {
    deleteMosqueReport: mutation.mutate,
    deleteMosqueReportAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                    ORPHANAGE REPORTS                                       */
/* -------------------------------------------------------------------------- */

// Hook to fetch orphanage reports
export function useOrphanageReports(beneficiaryId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['orphanageReports', beneficiaryId],
    queryFn: () => getOrphanageReports(beneficiaryId),
    enabled: !!beneficiaryId
  });

  return {
    orphanageReports: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create orphanage report
export function useCreateOrphanageReport() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async reportData => {
      const result = await createOrphanageReport({
        ...reportData,
        branch_id: branchId
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['orphanageReports'] });
      toast.success('Orphanage report added successfully');
    },
    onError: error => {
      console.error('Create orphanage report error:', error);
      toast.error('Failed to add orphanage report');
    }
  });

  return {
    createOrphanageReport: mutation.mutate,
    createOrphanageReportAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update orphanage report
export function useUpdateOrphanageReport() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateOrphanageReport(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['orphanageReports'] });
      toast.success('Orphanage report updated successfully');
    },
    onError: error => {
      console.error('Update orphanage report error:', error);
      toast.error('Failed to update orphanage report');
    }
  });

  return {
    updateOrphanageReport: mutation.mutate,
    updateOrphanageReportAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete orphanage report
export function useDeleteOrphanageReport() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteOrphanageReport(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['orphanageReports'] });
      toast.success('Orphanage report deleted successfully');
    },
    onError: error => {
      console.error('Delete orphanage report error:', error);
      toast.error('Failed to delete orphanage report');
    }
  });

  return {
    deleteOrphanageReport: mutation.mutate,
    deleteOrphanageReportAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

// Hook to fetch gallery items
export function useGalleryItems(beneficiaryId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['galleryItems', beneficiaryId],
    queryFn: () => getGalleryItems(beneficiaryId),
    enabled: !!beneficiaryId
  });

  return {
    galleryItems: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create gallery item
export function useCreateGalleryItem() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async itemData => {
      const result = await createGalleryItem({
        ...itemData,
        branch_id: branchId
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['galleryItems'] });
      toast.success('Gallery item added successfully');
    },
    onError: error => {
      console.error('Create gallery item error:', error);
      toast.error('Failed to add gallery item');
    }
  });

  return {
    createGalleryItem: mutation.mutate,
    createGalleryItemAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                    SPONSORSHIP SETTINGS                                    */
/* -------------------------------------------------------------------------- */

// Hook to fetch sponsorship types
export function useSponsorshipTypes() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['sponsorshipTypes', organization?.id, branchId],
    queryFn: () => getSponsorshipTypes(organization.id, branchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    sponsorshipTypes: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch sponsorship conditions
export function useSponsorshipConditions() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['sponsorshipConditions', organization?.id, branchId],
    queryFn: () => getSponsorshipConditions(organization.id, branchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    sponsorshipConditions: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch sponsorship costs
export function useSponsorshipCosts() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['sponsorshipCosts', organization?.id, branchId],
    queryFn: () => getSponsorshipCosts(organization.id, branchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    sponsorshipCosts: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch sponsorship priorities
export function useSponsorshipPriorities() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['sponsorshipPriorities', organization?.id, branchId],
    queryFn: () => getSponsorshipPriorities(organization.id, branchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    sponsorshipPriorities: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch sponsorship durations
export function useSponsorshipDurations() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['sponsorshipDurations', organization?.id, branchId],
    queryFn: () => getSponsorshipDurations(organization.id, branchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    sponsorshipDurations: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch sponsorship statuses
export function useSponsorshipStatuses() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['sponsorshipStatuses', organization?.id, branchId],
    queryFn: () => getSponsorshipStatuses(organization.id, branchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    sponsorshipStatuses: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

/* -------------------------------------------------------------------------- */
/*                    CONTACTS                                                */
/* -------------------------------------------------------------------------- */

// Hook to fetch contacts
export function useContacts() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['contacts', organization?.id, branchId],
    queryFn: () => getContacts(organization.id, branchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    contacts: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

/* -------------------------------------------------------------------------- */
/*                        GUARDIAN & LOCATION OPTIONS                         */
/* -------------------------------------------------------------------------- */

// Hook to fetch guardian father status options
export function useGuardianFatherStatus() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['guardianFatherStatus'],
    queryFn: getGuardianFatherStatus
  });

  return {
    guardianFatherStatus: data?.data || [],
    isLoading,
    error: error || data?.error
  };
}

// Hook to fetch guardian mother status options
export function useGuardianMotherStatus() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['guardianMotherStatus'],
    queryFn: getGuardianMotherStatus
  });

  return {
    guardianMotherStatus: data?.data || [],
    isLoading,
    error: error || data?.error
  };
}

// Hook to fetch guardian relationship options
export function useGuardianRelationship() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['guardianRelationship'],
    queryFn: getGuardianRelationship
  });

  return {
    guardianRelationship: data?.data || [],
    isLoading,
    error: error || data?.error
  };
}

// Hook to fetch location condition options
export function useLocationConditions() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['locationConditions'],
    queryFn: getLocationConditions
  });

  return {
    locationConditions: data?.data || [],
    isLoading,
    error: error || data?.error
  };
}

// Hook to fetch locations
export function useLocations(type) {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['locations', organization?.id, branchId, type],
    queryFn: () => getLocations(organization.id, branchId, type),
    enabled: isInitialized && !!organization?.id
  });

  return {
    locations: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

/* -------------------------------------------------------------------------- */
/*                    SPONSORSHIP PRIORITY & STATUS CRUD                      */
/* -------------------------------------------------------------------------- */

// Hook to create sponsorship priority
export function useCreateSponsorshipPriority() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async priorityData => {
      const result = await createSponsorshipPriority({
        ...priorityData,
        branch_id: branchId
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['sponsorshipPriorities']
      });
      toast.success('Priority created successfully');
    },
    onError: error => {
      console.error('Create priority error:', error);
      toast.error('Failed to create priority');
    }
  });

  return {
    createPriority: mutation.mutate,
    createPriorityAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update sponsorship priority
export function useUpdateSponsorshipPriority() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateSponsorshipPriority(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['sponsorshipPriorities']
      });
      toast.success('Priority updated successfully');
    },
    onError: error => {
      console.error('Update priority error:', error);
      toast.error('Failed to update priority');
    }
  });

  return {
    updatePriority: mutation.mutate,
    updatePriorityAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete sponsorship priority
export function useDeleteSponsorshipPriority() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteSponsorshipPriority(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['sponsorshipPriorities']
      });
      toast.success('Priority deleted successfully');
    },
    onError: error => {
      console.error('Delete priority error:', error);
      toast.error('Failed to delete priority');
    }
  });

  return {
    deletePriority: mutation.mutate,
    deletePriorityAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

// Hook to create sponsorship status
export function useCreateSponsorshipStatus() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async statusData => {
      const result = await createSponsorshipStatus({
        ...statusData,
        branch_id: branchId
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['sponsorshipStatuses']
      });
      toast.success('Status created successfully');
    },
    onError: error => {
      console.error('Create status error:', error);
      toast.error('Failed to create status');
    }
  });

  return {
    createStatus: mutation.mutate,
    createStatusAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update sponsorship status
export function useUpdateSponsorshipStatus() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateSponsorshipStatus(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['sponsorshipStatuses']
      });
      toast.success('Status updated successfully');
    },
    onError: error => {
      console.error('Update status error:', error);
      toast.error('Failed to update status');
    }
  });

  return {
    updateStatus: mutation.mutate,
    updateStatusAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete sponsorship status
export function useDeleteSponsorshipStatus() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteSponsorshipStatus(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['sponsorshipStatuses']
      });
      toast.success('Status deleted successfully');
    },
    onError: error => {
      console.error('Delete status error:', error);
      toast.error('Failed to delete status');
    }
  });

  return {
    deleteStatus: mutation.mutate,
    deleteStatusAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                    SPONSORSHIP TYPE CRUD                                   */
/* -------------------------------------------------------------------------- */

// Hook to create sponsorship type
export function useCreateSponsorshipType() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async typeData => {
      const result = await createSponsorshipType({
        ...typeData,
        branch_id: branchId
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sponsorshipTypes'] });
      toast.success('Sponsorship type created successfully');
    },
    onError: error => {
      console.error('Create sponsorship type error:', error);
      toast.error('Failed to create sponsorship type');
    }
  });

  return {
    createSponsorshipType: mutation.mutate,
    createSponsorshipTypeAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update sponsorship type
export function useUpdateSponsorshipType() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateSponsorshipType(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sponsorshipTypes'] });
      toast.success('Sponsorship type updated successfully');
    },
    onError: error => {
      console.error('Update sponsorship type error:', error);
      toast.error('Failed to update sponsorship type');
    }
  });

  return {
    updateSponsorshipType: mutation.mutate,
    updateSponsorshipTypeAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete sponsorship type
export function useDeleteSponsorshipType() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteSponsorshipType(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sponsorshipTypes'] });
      toast.success('Sponsorship type deleted successfully');
    },
    onError: error => {
      console.error('Delete sponsorship type error:', error);
      toast.error('Failed to delete sponsorship type');
    }
  });

  return {
    deleteSponsorshipType: mutation.mutate,
    deleteSponsorshipTypeAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                    SPONSORSHIP CONDITION CRUD                              */
/* -------------------------------------------------------------------------- */

// Hook to create sponsorship condition
export function useCreateSponsorshipCondition() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async conditionData => {
      const result = await createSponsorshipCondition({
        ...conditionData,
        branch_id: branchId
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['sponsorshipConditions']
      });
      toast.success('Sponsorship condition created successfully');
    },
    onError: error => {
      console.error('Create sponsorship condition error:', error);
      toast.error('Failed to create sponsorship condition');
    }
  });

  return {
    createSponsorshipCondition: mutation.mutate,
    createSponsorshipConditionAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update sponsorship condition
export function useUpdateSponsorshipCondition() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateSponsorshipCondition(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['sponsorshipConditions']
      });
      toast.success('Sponsorship condition updated successfully');
    },
    onError: error => {
      console.error('Update sponsorship condition error:', error);
      toast.error('Failed to update sponsorship condition');
    }
  });

  return {
    updateSponsorshipCondition: mutation.mutate,
    updateSponsorshipConditionAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete sponsorship condition
export function useDeleteSponsorshipCondition() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteSponsorshipCondition(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['sponsorshipConditions']
      });
      toast.success('Sponsorship condition deleted successfully');
    },
    onError: error => {
      console.error('Delete sponsorship condition error:', error);
      toast.error('Failed to delete sponsorship condition');
    }
  });

  return {
    deleteSponsorshipCondition: mutation.mutate,
    deleteSponsorshipConditionAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                    SPONSORSHIP COST CRUD                                   */
/* -------------------------------------------------------------------------- */

// Hook to create sponsorship cost
export function useCreateSponsorshipCost() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async costData => {
      const result = await createSponsorshipCost({
        ...costData,
        branch_id: branchId
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sponsorshipCosts'] });
      toast.success('Sponsorship cost created successfully');
    },
    onError: error => {
      console.error('Create sponsorship cost error:', error);
      toast.error('Failed to create sponsorship cost');
    }
  });

  return {
    createSponsorshipCost: mutation.mutate,
    createSponsorshipCostAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update sponsorship cost
export function useUpdateSponsorshipCost() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateSponsorshipCost(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sponsorshipCosts'] });
      toast.success('Sponsorship cost updated successfully');
    },
    onError: error => {
      console.error('Update sponsorship cost error:', error);
      toast.error('Failed to update sponsorship cost');
    }
  });

  return {
    updateSponsorshipCost: mutation.mutate,
    updateSponsorshipCostAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete sponsorship cost
export function useDeleteSponsorshipCost() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteSponsorshipCost(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sponsorshipCosts'] });
      toast.success('Sponsorship cost deleted successfully');
    },
    onError: error => {
      console.error('Delete sponsorship cost error:', error);
      toast.error('Failed to delete sponsorship cost');
    }
  });

  return {
    deleteSponsorshipCost: mutation.mutate,
    deleteSponsorshipCostAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                    SPONSORSHIP DURATION CRUD                               */
/* -------------------------------------------------------------------------- */

// Hook to create sponsorship duration
export function useCreateSponsorshipDuration() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async durationData => {
      const result = await createSponsorshipDuration({
        ...durationData,
        branch_id: branchId
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['sponsorshipDurations']
      });
      toast.success('Sponsorship duration created successfully');
    },
    onError: error => {
      console.error('Create sponsorship duration error:', error);
      toast.error('Failed to create sponsorship duration');
    }
  });

  return {
    createSponsorshipDuration: mutation.mutate,
    createSponsorshipDurationAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update sponsorship duration
export function useUpdateSponsorshipDuration() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateSponsorshipDuration(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['sponsorshipDurations']
      });
      toast.success('Sponsorship duration updated successfully');
    },
    onError: error => {
      console.error('Update sponsorship duration error:', error);
      toast.error('Failed to update sponsorship duration');
    }
  });

  return {
    updateSponsorshipDuration: mutation.mutate,
    updateSponsorshipDurationAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete sponsorship duration
export function useDeleteSponsorshipDuration() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteSponsorshipDuration(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['sponsorshipDurations']
      });
      toast.success('Sponsorship duration deleted successfully');
    },
    onError: error => {
      console.error('Delete sponsorship duration error:', error);
      toast.error('Failed to delete sponsorship duration');
    }
  });

  return {
    deleteSponsorshipDuration: mutation.mutate,
    deleteSponsorshipDurationAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                    SPONSORSHIP MANAGEMENT (Shared Sponsorships)            */
/* -------------------------------------------------------------------------- */

// Hook to fetch sponsorship management stats
export function useSponsorshipManagementStats() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId, isMainBranch } = useBranchPermission();
  // Main branch (HQ) sees stats across all branches
  const effectiveBranchId = isMainBranch ? null : branchId;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'sponsorshipManagementStats',
      organization?.id,
      effectiveBranchId
    ],
    queryFn: () =>
      getSponsorshipManagementStats(organization.id, effectiveBranchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    stats: data?.data || {
      total: 0,
      active: 0,
      pending: 0,
      ended: 0,
      uniqueBeneficiaries: 0
    },
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch sponsorships for management page
export function useSponsorshipsForManagement(filters = {}) {
  const { organization, isInitialized } = useAuthContext();
  const { branchId, isMainBranch } = useBranchPermission();
  // Main branch (HQ) manages sponsorships across all branches
  const effectiveBranchId = isMainBranch ? null : branchId;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'sponsorshipsForManagement',
      organization?.id,
      effectiveBranchId,
      filters
    ],
    queryFn: () =>
      getSponsorshipsForManagement(organization.id, effectiveBranchId, filters),
    enabled: isInitialized && !!organization?.id,
    staleTime: 0 // Always refetch for fresh sponsorship data
  });

  return {
    sponsorships: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch sponsorship detail for a specific beneficiary
export function useSponsorshipDetail(beneficiaryId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['sponsorshipDetail', beneficiaryId],
    queryFn: () => getSponsorshipDetail(beneficiaryId),
    enabled: !!beneficiaryId,
    staleTime: 0 // Always refetch for fresh sponsorship data
  });

  return {
    sponsorshipDetail: data?.data || null,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create a shared sponsorship
export function useCreateSharedSponsorship() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async sponsorshipData => {
      const result = await createSharedSponsorship(sponsorshipData);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['sponsorshipsForManagement']
      });
      await queryClient.invalidateQueries({
        queryKey: ['sponsorshipManagementStats']
      });
      await queryClient.invalidateQueries({ queryKey: ['sponsorshipDetail'] });
      await queryClient.invalidateQueries({
        queryKey: ['donorsWithRemainingDonations']
      });
      toast.success('Sponsorship created successfully');
    },
    onError: error => {
      console.error('Create shared sponsorship error:', error);
      toast.error('Failed to create sponsorship');
    }
  });

  return {
    createSharedSponsorship: mutation.mutate,
    createSharedSponsorshipAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to create sponsorships in batch with validation
export function useCreateSponsorshipsBatch() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ items, beneficiaryId, branchId }) => {
      const result = await createSponsorshipsBatch(
        items,
        beneficiaryId,
        branchId
      );

      if (result.validationFailed) {
        // Validation errors - throw with specific error info
        const error = new Error('Validation failed');
        error.validationErrors = result.error;
        throw error;
      }

      if (result.error) {
        const error = new Error(result.error);
        error.partialSuccess = result.partialSuccess;
        throw error;
      }

      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['sponsorshipsForManagement']
      });
      await queryClient.invalidateQueries({
        queryKey: ['sponsorshipManagementStats']
      });
      await queryClient.invalidateQueries({ queryKey: ['sponsorshipDetail'] });
      await queryClient.invalidateQueries({
        queryKey: ['donorsWithRemainingDonations']
      });
    },
    onError: error => {
      console.error('Create sponsorships batch error:', error);
    }
  });

  return {
    createSponsorshipsBatch: mutation.mutate,
    createSponsorshipsBatchAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to validate sponsorship items before submission
export function useValidateSponsorshipItems() {
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);

  const validate = async items => {
    setIsValidating(true);
    try {
      const result = await validateSponsorshipItems(items);
      setValidationResult(result);
      return result;
    } finally {
      setIsValidating(false);
    }
  };

  const clearValidation = () => {
    setValidationResult(null);
  };

  return {
    validate,
    clearValidation,
    isValidating,
    validationResult
  };
}

// Hook to end a sponsorship
export function useEndSponsorship() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async sponsorshipId => {
      const result = await endSponsorship(sponsorshipId);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['sponsorshipsForManagement']
      });
      await queryClient.invalidateQueries({
        queryKey: ['sponsorshipManagementStats']
      });
      await queryClient.invalidateQueries({ queryKey: ['sponsorshipDetail'] });
      toast.success('Sponsorship ended successfully');
    },
    onError: error => {
      console.error('End sponsorship error:', error);
      toast.error('Failed to end sponsorship');
    }
  });

  return {
    endSponsorship: mutation.mutate,
    endSponsorshipAsync: mutation.mutateAsync,
    isEnding: mutation.isPending,
    error: mutation.error
  };
}

// Hook to re-point a sponsorship at a different funding donation, keeping the
// sponsorship (and its history) intact - the non-destructive alternative to
// end + recreate.
export function useChangeSponsorshipPayment() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async payload => {
      const result = await changeSponsorshipPayment(payload);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sponsorships'] });
      await queryClient.invalidateQueries({ queryKey: ['sponsorship'] });
      await queryClient.invalidateQueries({ queryKey: ['donorSponsorships'] });
      await queryClient.invalidateQueries({
        queryKey: ['sponsorshipsForManagement']
      });
      await queryClient.invalidateQueries({
        queryKey: ['sponsorshipManagementStats']
      });
      await queryClient.invalidateQueries({ queryKey: ['sponsorshipDetail'] });
      await queryClient.invalidateQueries({
        queryKey: ['donorsWithRemainingDonations']
      });
      toast.success('Sponsorship payment changed');
    },
    onError: error => {
      console.error('Change sponsorship payment error:', error);
      toast.error(error.message || 'Failed to change sponsorship payment');
    }
  });

  return {
    changePayment: mutation.mutate,
    changePaymentAsync: mutation.mutateAsync,
    isChangingPayment: mutation.isPending,
    error: mutation.error
  };
}

// Hook to fetch donors with remaining donation amounts.
// Pass { includeAll: true } to get every paid line item (not just
// sponsorship packages with remaining balance).
export function useDonorsWithRemainingDonations({ includeAll = false } = {}) {
  const { organization, selectedBranchId, isInitialized } = useAuthContext();
  // 'all' is the sentinel for the "All Branches" view - it must NOT be passed
  // as a branch filter, otherwise the query runs `branch_id = 'all'` against a
  // uuid column and errors, returning zero donors (so nothing shows up to
  // assign). Treat it as no branch filter, like useBranchPermission does.
  const branchId =
    selectedBranchId && selectedBranchId !== 'all' ? selectedBranchId : null;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'donorsWithRemainingDonations',
      organization?.id,
      branchId,
      includeAll
    ],
    queryFn: () =>
      getDonorsWithRemainingDonations(organization.id, branchId, {
        includeAll
      }),
    enabled: isInitialized && !!organization?.id,
    staleTime: 0
  });

  return {
    donorsWithDonations: data?.data || EMPTY_ARRAY,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

/* -------------------------------------------------------------------------- */
/*                           FACILITY CLOSURE                                 */
/* -------------------------------------------------------------------------- */

// kind: 'school' | 'mosque' | 'hospital' | 'orphanage'
export function useCloseFacility(kind) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, reason }) => {
      const result = await closeFacility(kind, id, reason);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [`${kind}s`] });
      toast.success(
        `${
          kind.charAt(0).toUpperCase() + kind.slice(1)
        } closed - open enrolments ended`
      );
    },
    onError: error => {
      console.error(`Close ${kind} error:`, error);
      toast.error(error.message || `Failed to close ${kind}`);
    }
  });

  return {
    closeFacility: mutation.mutate,
    closeFacilityAsync: mutation.mutateAsync,
    isClosing: mutation.isPending,
    error: mutation.error
  };
}

export function useReopenFacility(kind) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await reopenFacility(kind, id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [`${kind}s`] });
      toast.success(`${kind.charAt(0).toUpperCase() + kind.slice(1)} reopened`);
    },
    onError: error => {
      console.error(`Reopen ${kind} error:`, error);
      toast.error(error.message || `Failed to reopen ${kind}`);
    }
  });

  return {
    reopenFacility: mutation.mutate,
    reopenFacilityAsync: mutation.mutateAsync,
    isReopening: mutation.isPending,
    error: mutation.error
  };
}
